import { useState, useEffect, useCallback, useRef } from "react";
import communityService from "../services/communityService";

/**
 * Custom hook for fetching community-specific sidebar data
 * Includes: Community Info, Rules, Top Contributors, Trending Topics, Moderators
 */
export function useCommunitySpecificSidebar(communityId, options = {}) {
  const [sidebar, setSidebar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Track if component is mounted
  const isMounted = useRef(true);
  // Track in-flight request
  const fetchInProgress = useRef(false);
  // Store last successful data
  const lastData = useRef(null);

  const fetchSidebar = useCallback(async (isBackgroundRefresh = false) => {
    if (!communityId) return;
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;

    if (!isBackgroundRefresh && !lastData.current) {
      setLoading(true);
    }

    try {
      const response = await communityService.getCommunitySpecificSidebar(communityId, {
        topContributorsLimit: options.topContributorsLimit || 5,
        trendingTopicsLimit: options.trendingTopicsLimit || 5,
        rulesLimit: options.rulesLimit || 5,
      });

      if (isMounted.current) {
        const data = response?.data || response;
        setSidebar(data);
        lastData.current = data;
        setError(null);
      }
    } catch (err) {
      if (isMounted.current) {
setError(err);
        // Keep showing last successful data on error
        if (lastData.current) {
          setSidebar(lastData.current);
        }
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      fetchInProgress.current = false;
    }
  }, [communityId, options.topContributorsLimit, options.trendingTopicsLimit, options.rulesLimit]);

  useEffect(() => {
    isMounted.current = true;
    fetchSidebar();

    // Auto-refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchSidebar(true);
    }, 5 * 60 * 1000);

    return () => {
      isMounted.current = false;
      clearInterval(refreshInterval);
    };
  }, [fetchSidebar]);

  const refetch = useCallback(() => {
    return fetchSidebar(false);
  }, [fetchSidebar]);

  return { 
    sidebar, 
    loading, 
    error, 
    refetch,
    // Convenience accessors
    communityInfo: sidebar?.communityInfo || null,
    rules: sidebar?.rules || [],
    topContributors: sidebar?.topContributors || [],
    trendingTopics: sidebar?.trendingTopics || [],
    moderators: sidebar?.moderators || null,
  };
}

export default useCommunitySpecificSidebar;
